/**
 * PublicEmptyState — the panel shown in place of a record grid when there is
 * nothing to draw.
 *
 * It exists because a zero-record response used to render zero DOM on the
 * custom-layout list: `records.map()` over an empty array produces nothing, so
 * the page ended at the search bar and looked broken rather than empty.
 *
 * TWO CAUSES, TWO MESSAGES. "There is nothing here" and "your search matched
 * nothing" are different situations and need different wording — telling a
 * visitor to adjust filters they never set is worse than saying nothing. The
 * caller decides which by passing `variant`; it knows whether a query is
 * active and this component does not.
 *
 * Copy is passed in rather than built from the table name. A caller that wants
 * the table's own wording supplies it; the defaults here stay generic so this
 * component is safe to reuse on any table in any app.
 */

import React from 'react';

const COPY = {
  empty: {
    icon: 'bi-calendar4-week',
    title: 'Nothing here yet',
    subtitle: 'New entries appear here the moment they are published. Please stay tuned.',
  },
  'no-results': {
    icon: 'bi-search',
    title: 'No matches found',
    subtitle: 'Nothing matched your search or filters. Try a different term, or clear the filters to see everything.',
  },
};

/*
 * `icon`, `action`, `clearLabel`, `titleColor` and `subtitleColor` were added
 * for the feed's
 * My Transactions page. EVERY ONE DEFAULTS TO THE PREVIOUS BEHAVIOUR: the
 * icon falls back to the variant's own, no action renders no button, and the
 * colours are the portal slate greys this block has always used. A caller
 * that passes none of them -- the public events list above all -- renders
 * exactly what it rendered before. There is a test for that.
 */
export default function PublicEmptyState({
  variant = 'empty',
  accentColor = '#FF6A1A',
  title,
  subtitle,
  onClear,
  icon,
  action,
  titleColor = '#0f172a',
  subtitleColor = 'var(--color-text-secondary, #94a3b8)',
  clearLabel = 'Clear search',
}) {
  const base = COPY[variant] || COPY.empty;
  const heading = title || base.title;
  const body = subtitle || base.subtitle;
  const iconClass = icon || base.icon;

  // Scoped to this block's own class names so it cannot leak into a builder's
  // custom CSS, which shares the page.
  const css = `
    .ahv-empty {
      text-align: center;
      padding: 48px 24px;
      background: #fff;
      border-radius: 18px;
      box-shadow: 0 2px 10px rgba(0,0,0,.06), 0 8px 30px ${accentColor}14;
      border: 1.5px solid ${accentColor}22;
    }
    .ahv-empty-scene {
      position: relative;
      width: 210px;
      height: 124px;
      margin: 0 auto 20px;
    }
    .ahv-empty-scene svg { position: absolute; inset: 0; width: 100%; height: 100%; }
    .ahv-empty-badge {
      position: absolute; left: 50%; top: 14px; margin-left: -38px;
      width: 76px; height: 76px; border-radius: 50%;
      background: linear-gradient(135deg, ${accentColor}21 0%, ${accentColor}4d 100%);
      box-shadow: 0 6px 24px ${accentColor}33;
      display: flex; align-items: center; justify-content: center;
      z-index: 3;
    }
    .ahv-empty-badge i { font-size: 33px; color: ${accentColor}; }
    .ahv-empty-ring {
      position: absolute; left: 50%; top: 14px; margin-left: -38px;
      width: 76px; height: 76px; border-radius: 50%;
      border: 1.5px solid ${accentColor}4d;
      animation: ahvEmptyPulse 2.8s ease-out infinite;
      z-index: 1;
    }
    .ahv-empty-ring:nth-of-type(2) { animation-delay: .9s; }
    .ahv-empty-ring:nth-of-type(3) { animation-delay: 1.8s; }
    .ahv-empty-dash { stroke-dasharray: 5 7; animation: ahvEmptyMarch 9s linear infinite; }
    .ahv-empty-title {
      font-weight: 700; font-size: 1.22rem; color: ${titleColor}; margin: 0 0 9px;
    }
    .ahv-empty-sub {
      color: ${subtitleColor};
      font-size: .9rem; line-height: 1.6; margin: 0 auto; max-width: 380px;
    }
    .ahv-empty-action {
      margin-top: 20px; display: inline-flex; align-items: center; gap: 8px;
      background: ${accentColor}; color: #fff; border: 0;
      border-radius: var(--button-radius, .5rem);
      padding: 10px 20px; font-size: .88rem; font-weight: 700; cursor: pointer;
      transition: filter 150ms ease;
    }
    .ahv-empty-action:hover { filter: brightness(1.07); color: #fff; }
    .ahv-empty-clear {
      margin-top: 20px; display: inline-flex; align-items: center; gap: 8px;
      background: transparent; color: ${accentColor};
      border: 1.5px solid ${accentColor}59;
      border-radius: var(--button-radius, .5rem);
      padding: 9px 18px; font-size: .86rem; font-weight: 600; cursor: pointer;
      transition: background 150ms ease, color 150ms ease;
    }
    .ahv-empty-clear:hover { background: ${accentColor}; color: #fff; }
    @keyframes ahvEmptyPulse {
      0%   { transform: scale(.72); opacity: .8; }
      100% { transform: scale(1.32); opacity: 0; }
    }
    @keyframes ahvEmptyMarch { to { stroke-dashoffset: -120; } }
    /* Respect a visitor who has asked the OS for less motion. */
    @media (prefers-reduced-motion: reduce) {
      .ahv-empty-ring, .ahv-empty-dash { animation: none; }
      .ahv-empty-ring { opacity: .35; }
    }
    @media (max-width: 576px) {
      .ahv-empty { padding: 36px 18px; }
      .ahv-empty-scene { width: 180px; height: 110px; }
      .ahv-empty-title { font-size: 1.08rem; }
    }
  `;

  return (
    <>
      <style>{css}</style>
      <div className="ahv-empty">
        <div className="ahv-empty-scene">
          <svg viewBox="0 0 210 124" fill="none" aria-hidden="true" focusable="false">
            <ellipse cx="105" cy="108" rx="60" ry="7" fill={accentColor} opacity=".10" />
            <path
              className="ahv-empty-dash"
              d="M22 90 Q 105 54 188 90"
              stroke={accentColor}
              strokeWidth="2.5"
              strokeLinecap="round"
              opacity=".45"
            />
            <circle cx="40" cy="38" r="4" fill={accentColor} opacity=".32" />
            <circle cx="172" cy="50" r="5.5" fill={accentColor} opacity=".20" />
            <circle cx="157" cy="24" r="2.8" fill={accentColor} opacity=".40" />
          </svg>
          <span className="ahv-empty-ring" />
          <span className="ahv-empty-ring" />
          <span className="ahv-empty-ring" />
          <div className="ahv-empty-badge">
            <i className={`bi ${iconClass}`} />
          </div>
        </div>

        <p className="ahv-empty-title">{heading}</p>
        <p className="ahv-empty-sub">{body}</p>

        {action && (
          action.to ? (
            <a className="ahv-empty-action" href={action.to}>
              {action.icon && <i className={`bi ${action.icon}`} />} {action.label}
            </a>
          ) : (
            <button type="button" className="ahv-empty-action" onClick={action.onClick}>
              {action.icon && <i className={`bi ${action.icon}`} />} {action.label}
            </button>
          )
        )}

        {onClear && (
          <button type="button" className="ahv-empty-clear" onClick={onClear}>
            <i className="bi bi-x-circle" /> {clearLabel}
          </button>
        )}
      </div>
    </>
  );
}
